<template lang='pug'>
  div
    v-row.text-center
      v-col
        bionic-metrics-test-status-indicator(:status='test.status' :size='28')
        .text-subtitle-1.grey--text Status
      v-col
        .text-h5.font-weight-light {{durationSeconds}}s
        .text-subtitle-1.grey--text Duration
      v-col
        .text-h5.font-weight-light {{actions}}
        .text-subtitle-1.grey--text Macro Actions
      v-col
        .text-h5.font-weight-light {{screens}}
        .text-subtitle-1.grey--text Screenshots
      v-col
        .text-h5.font-weight-light {{browser}}
        .text-subtitle-1.grey--text Browser Warnings/Errors
    bionic-metrics-chart(v-if='chartOptions' :options='chartOptions' height='25px' @click='onClick')
</template>

<script>
import moment from 'moment';
import {first, get, size} from 'lodash';
import BionicMetricsChart from './Chart';
import BionicMetricsTestStatusIndicator from './TestStatusIndicator';
import ColorMixin from '../mixins/color.mixin';

export default {
  name: 'BionicMetricsTestBreakdown',
  components: {BionicMetricsTestStatusIndicator, BionicMetricsChart},
  mixins: [ColorMixin],
  props: {
    test: {
      type: Object,
      required: true
    }
  },
  mounted() {
    this.load();
  },
  computed: {
    screens() {
      return size(this.test.screens) + 1;
    },
    browser() {
      return size(this.test.browserLogs);
    },
    actions() {
      return size(this.test.monitor);
    },
    durationSeconds() {
      if (!this.test || !this.test.durationUs) return 0;
      const seconds = this.test.durationUs / 1000000;
      const toTenths = (Math.round(seconds * 10) / 10).toFixed(1);
      return toTenths;
    },
    duration() {
      if (!this.test || !this.test.durationUs) return 0;
      const ms = Math.round(this.test.durationUs / 1000);
      return moment.duration(ms, 'ms').seconds();
    }
  },
  methods: {
    load() {
      const series = this.calculateSeries();
      const max = Math.round(this.test.durationUs / 1000);
      this.chartOptions = {
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'shadow'
          },
          // https://echarts.apache.org/en/option.html#tooltip.formatter
          formatter(params) {
            return params.data.tt;
          },
          position(point) {
            return [point[0] - 50, 50];
          }
        },
        grid: {
          left: 0,
          top: 0,
          right: 0,
          bottom: 0
        },
        xAxis: {
          show: false,
          type: 'value',
          max
        },
        yAxis: {
          show: false,
          type: 'category'
        },
        series
      };
    },
    getDataPoint(item) {
      const ms = Math.round(item.durationUs / 1000);
      const {type, gitName, lineNumber, line, name, guid} = item;
      let tt = `${ms}ms ${name}`;
      let to = null;
      if (type && gitName && line) tt += `<br>${type}/${gitName}:${lineNumber} ${name} ${line.trim()}`;
      if (type && guid && lineNumber) to = `/${type}/${guid}?line=${lineNumber}`;
      const color = this.getColor(name);
      return {
        barWidth: 25,
        name,
        type: 'bar',
        stack: 'this',
        data: [{
          name,
          itemStyle: {color},
          value: ms,
          tt,
          to
        }]
      };
    },
    neglibable(microseconds) {
      if (!this.test || !this.test.durationUs) return true;
      return (microseconds / this.test.durationUs) < 0.05; // less than 5%
    },
    calculateSeries() {
      const series = [];
      const firstEvent = first(this.test.monitor);
      const timeToFirstUs = firstEvent ? firstEvent.startUs - this.test.startUs : 0;
      if (!this.neglibable(timeToFirstUs)) {
        series.push(this.getDataPoint({name: 'startUp', durationUs: timeToFirstUs}));
      }
      for (const [i, item] of this.test.monitor.entries()) {
        series.push(this.getDataPoint(item));
        const nextTick = this.getNextTick(i);
        const microsecsTillNextEvent = this.gapUs(item, nextTick);
        if (!this.neglibable(microsecsTillNextEvent)) {
          series.push(this.getDataPoint({
            name: 'gap',
            durationUs: microsecsTillNextEvent
          }));
        }
      }
      return series;
    },
    getNextTick(fromIndex) {
      const next = get(this.test.monitor, fromIndex + 1);
      if (next) return next.startUs;
      return this.test.startUs + this.test.durationUs;
    },
    gapUs(item, nextTick) {
      if (!item || !nextTick) return 0;
      return Math.round((nextTick - (item.startUs + item.durationUs)));
    },
    onClick(params) {
      const to = get(params, 'data.to');
      if (to) this.$router.push(to);
    }
  },
  data() {
    return {
      loading: false,
      chartOptions: null
    };
  }
};
</script>

<style scoped>

</style>
